import {
  Card,
  CardContent,
  cn,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Spinner,
} from "@forge/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChartLine } from "lucide-react";
import type { ReactNode } from "react";
import { PageHeader } from "../../components/page-header";
import {
  ProfitQualityBadge,
  ProfitReadiness,
} from "../../components/profit-readiness";
import { Sparkline, TrendChart } from "../../components/trend-chart";
import { api, unwrap } from "../../lib/api";
import { formatBRL, formatPercent } from "../../lib/format";

type Bucket = "week" | "month";
type Range = "7d" | "30d" | "90d";
type PanelSearch = { bucket?: Bucket; range?: Range };

const PERIOD_LABEL: Record<string, string> = {
  "7d": "Últimos 7 dias",
  "30d": "Últimos 30 dias",
  "90d": "Últimos 90 dias",
  week: "Semana a semana",
  month: "Mês a mês",
};

export const Route = createFileRoute("/_app/dashboard")({
  validateSearch: (search: Record<string, unknown>): PanelSearch => {
    if (search.range === "7d" || search.range === "30d" || search.range === "90d") {
      return { range: search.range };
    }
    return { bucket: search.bucket === "week" ? "week" : "month" };
  },
  component: BusinessPanelPage,
});

function formatSignedMoney(value: string) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount === 0) return formatBRL(0);
  const formatted = formatBRL(Math.abs(amount));
  return amount > 0 ? `+${formatted}` : `−${formatted}`;
}

function brDay(ymd: string) {
  const [, month, day] = ymd.split("-");
  return day && month ? `${day}/${month}` : ymd;
}

function formatDeltaPct(ratio: number | null) {
  if (ratio == null || !Number.isFinite(ratio)) return null;
  const pct = (ratio * 100).toLocaleString("pt-BR", {
    maximumFractionDigits: 0,
  });
  return `${ratio > 0 ? "+" : ""}${pct}%`;
}

function BusinessPanelPage() {
  const search = Route.useSearch();
  const bucket = search.bucket ?? "month";
  const range = search.range;
  const tab = range ?? bucket;
  const navigate = Route.useNavigate();
  const report = useQuery({
    queryKey: ["dashboard", bucket, range],
    queryFn: async () =>
      unwrap(
        await api.dashboard.get({
          query: range ? { range } : { bucket },
        }),
      ),
    placeholderData: keepPreviousData,
  });

  const data = report.data;
  const vs = data?.previous
    ? `vs. ${data.previous.label.toLowerCase()}`
    : range
      ? "vs. janela anterior"
      : bucket === "week"
        ? "vs. semana passada"
        : "vs. mês passado";
  const hasSeries = Boolean(data?.periods.some((row) => row.orders > 0));

  return (
    <>
      <PageHeader
        title="Painel"
        description="Como a loja evoluiu — o cockpit continua sendo o que fazer agora."
        actions={
          <div className="flex min-w-0 items-center gap-2">
            <span className="hidden text-2xs font-medium text-ink-muted sm:inline">
              Período
            </span>
            <Select
              value={tab}
              itemToStringLabel={(value) =>
                PERIOD_LABEL[String(value)] ?? String(value)
              }
              onValueChange={(value) => {
                if (value == null) return;
                if (value === "7d" || value === "30d" || value === "90d") {
                  void navigate({ to: "/dashboard", search: { range: value } });
                  return;
                }
                if (value === "week" || value === "month") {
                  void navigate({
                    to: "/dashboard",
                    search: { bucket: value },
                  });
                }
              }}
            >
              <SelectTrigger className="min-w-44" aria-label="Período" />
              <SelectContent>
                <SelectItem value="7d">Últimos 7 dias</SelectItem>
                <SelectItem value="30d">Últimos 30 dias</SelectItem>
                <SelectItem value="90d">Últimos 90 dias</SelectItem>
                <SelectItem value="week">Semana a semana</SelectItem>
                <SelectItem value="month">Mês a mês</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      {report.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-5 text-ink-muted" />
        </div>
      ) : data ? (
        <div className="flex flex-col gap-3">
          {data.readiness && !data.readiness.ready ? (
            <ProfitReadiness readiness={data.readiness} />
          ) : null}
          {data.adsApproximate ? (
            <p className="text-2xs text-ink-muted">
              Ads e extrato do Mercado Livre são mensais — neste recorte o
              gasto aparece por aproximação, sem fatiar o mês.
            </p>
          ) : null}

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Metric
              label="Receita"
              value={formatBRL(data.current.revenue)}
              delta={formatDeltaPct(data.delta.revenue.pct)}
              vs={vs}
              tone={toneFromDelta(data.delta.revenue.pct)}
            />
            <Metric
              label="Lucro"
              value={
                data.current.quality === "empty"
                  ? "—"
                  : formatBRL(data.current.profit ?? "0")
              }
              delta={formatDeltaPct(data.delta.profit.pct)}
              vs={vs}
              tone={toneFromDelta(data.delta.profit.pct)}
              badge={<ProfitQualityBadge quality={data.current.quality} />}
              emphasize
            />
            <Metric
              label="Pedidos"
              value={String(data.current.orders)}
              delta={formatDeltaPct(data.delta.orders.pct)}
              vs={vs}
              tone={toneFromDelta(data.delta.orders.pct)}
            />
            <Metric
              label="Margem"
              value={formatPercent(data.current.margin)}
              delta={
                data.delta.margin.abs != null
                  ? `${data.delta.margin.abs > 0 ? "+" : ""}${(data.delta.margin.abs * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} pp`
                  : null
              }
              vs={vs}
              tone={toneFromDelta(data.delta.margin.abs)}
            />
          </div>

          <Card>
            <CardContent className="p-4">
              <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                <div>
                  <p className="text-[0.8125rem] font-semibold text-ink">
                    Receita e lucro
                  </p>
                  <p className="mt-0.5 text-2xs text-ink-muted">
                    Barras são pedidos.{" "}
                    <span className="text-ember">Receita</span>
                    {" · "}
                    <span className="text-ink">Lucro</span>
                    {data.current.current
                      ? " · período em curso"
                      : null}
                  </p>
                </div>
                <Link
                  to="/profit"
                  className="text-2xs font-medium text-ember-strong [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
                >
                  Ver o mês no Lucro →
                </Link>
              </div>
              {hasSeries ? (
                <TrendChart
                  data={data.periods.map((row) => ({
                    axis: row.axis,
                    label: row.label,
                    revenue: Number(row.revenue),
                    profit: row.profit != null ? Number(row.profit) : 0,
                    orders: row.orders,
                  }))}
                />
              ) : (
                <EmptyState
                  icon={<ChartLine />}
                  title="Ainda sem pedidos pagos neste recorte"
                  description="O painel passa a desenhar a curva assim que o primeiro pedido pago entrar na janela."
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-[0.8125rem] font-semibold text-ink">
                {variationTitle(data.variation.profitDelta, tab)}
              </p>
              {data.current.current ? (
                <p className="mt-1 text-2xs text-ink-muted">
                  {data.current.label} ainda não fechou — a comparação é com{" "}
                  {`${data.previous?.label.toLowerCase() ?? "o período anterior"}.`}
                </p>
              ) : null}
              {data.variation.drivers.length > 0 ? (
                <ul className="mt-3 divide-y divide-line">
                  {data.variation.drivers.map((driver) => {
                    const impact = Number(driver.impact);
                    return (
                      <li
                        key={driver.key}
                        className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
                      >
                        {driver.listingId ? (
                          <Link
                            to="/listings/$id"
                            params={{ id: driver.listingId }}
                            className="min-w-0 truncate text-[0.8125rem] text-ink [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
                          >
                            {driver.label}
                          </Link>
                        ) : (
                          <span className="min-w-0 truncate text-[0.8125rem] text-ink">
                            {driver.label}
                          </span>
                        )}
                        <span
                          className={cn(
                            "shrink-0 font-mono text-2xs",
                            impact > 0
                              ? "text-success"
                              : impact < 0
                                ? "text-danger"
                                : "text-ink-muted",
                          )}
                        >
                          {formatSignedMoney(driver.impact)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="mt-2 text-2xs text-ink-muted">
                  Nada material mudou na decomposição de lucro frente ao período
                  anterior.
                </p>
              )}
            </CardContent>
          </Card>

          {data.visitsSeries && data.visitsSeries.length > 0 ? (
            <Card>
              <CardContent className="p-4">
                <p className="text-[0.8125rem] font-semibold text-ink">
                  Visitas da loja
                </p>
                <p className="mt-0.5 text-2xs text-ink-muted">
                  {data.visitsSeries
                    .reduce((acc, row) => acc + row.visits, 0)
                    .toLocaleString("pt-BR")}{" "}
                  visitas no recorte · série diária do Mercado Livre. Passe o
                  mouse para ver o dia.
                </p>
                <div className="mt-3">
                  <Sparkline
                    values={data.visitsSeries.map((row) => row.visits)}
                    labels={data.visitsSeries.map((row) => brDay(row.date))}
                    className="h-14 w-full"
                  />
                </div>
              </CardContent>
            </Card>
          ) : null}

          {data.snapshots.listings.length > 0 ? (
            <Card>
              <CardContent className="p-4">
                <p className="text-[0.8125rem] font-semibold text-ink">
                  Visitas dos anúncios
                </p>
                <p className="mt-0.5 text-2xs text-ink-muted">
                  {range
                    ? "Visitas diárias no recorte escolhido."
                    : "Janela móvel de 30 dias, amostrada toda semana quando a série diária ainda não entrou."}
                </p>
                <ul className="mt-3 divide-y divide-line">
                  {data.snapshots.listings.map((listing) => {
                    const last = listing.series[listing.series.length - 1];
                    return (
                      <li
                        key={listing.listingId}
                        className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                      >
                        <Link
                          to="/listings/$id"
                          params={{ id: listing.listingId }}
                          className="min-w-0 flex-1 truncate text-[0.8125rem] text-ink [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
                        >
                          {listing.title}
                        </Link>
                        <Sparkline
                          values={listing.series.map((row) => row.visits30d)}
                          className="h-8 w-20 shrink-0"
                        />
                        <span className="w-12 shrink-0 text-right font-mono text-2xs text-ink">
                          {last?.visits30d ?? "—"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          ) : (
            <p className="text-2xs text-ink-muted">
              Visitas diárias entram depois de importar visitas em Conexões.
            </p>
          )}
        </div>
      ) : (
        <EmptyState
          icon={<ChartLine />}
          title="Não foi possível carregar o painel"
          description="Tente de novo em instantes."
        />
      )}
    </>
  );
}

function variationTitle(profitDelta: string, tab: string) {
  const amount = Number(profitDelta);
  const when =
    tab === "week"
      ? "nesta semana"
      : tab === "month"
        ? "neste mês"
        : tab === "7d"
          ? "nos últimos 7 dias"
          : tab === "90d"
            ? "nos últimos 90 dias"
            : "nos últimos 30 dias";
  if (!Number.isFinite(amount) || amount === 0) {
    return `O lucro ficou estável ${when}`;
  }
  return amount > 0
    ? `O lucro subiu ${formatBRL(amount)} ${when}`
    : `O lucro caiu ${formatBRL(Math.abs(amount))} ${when}`;
}

function toneFromDelta(value: number | null | undefined) {
  if (value == null || value === 0) return "neutral" as const;
  return value > 0 ? ("up" as const) : ("down" as const);
}

function Metric({
  label,
  value,
  delta,
  vs,
  tone,
  badge,
  emphasize,
}: {
  label: string;
  value: string;
  delta: string | null;
  vs: string;
  tone: "up" | "down" | "neutral";
  badge?: ReactNode;
  emphasize?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-3.5" data-slot="metric">
        <div className="flex items-center justify-between gap-2">
          <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
            {label}
          </p>
          {badge}
        </div>
        <p
          className={cn(
            "mt-1 font-mono text-xl font-medium tracking-tight",
            emphasize ? "text-ember-strong" : "text-ink",
          )}
        >
          {value}
        </p>
        <p
          className={cn(
            "mt-1 text-2xs",
            tone === "up" && "text-success",
            tone === "down" && "text-danger",
            tone === "neutral" && "text-ink-muted",
          )}
        >
          {delta ? `${delta} ${vs}` : vs}
        </p>
      </CardContent>
    </Card>
  );
}
